import { FileDownload, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DataTable from "react-data-table-component";
import { useCallback, useMemo, useState } from "react";
import { FormSummaryModal } from "../components/dialogs/FormSummaryModal";
import {
  SoulDataModel,
  SoulQuestionnaireForm,
} from "../data/models/Soul/SoulDataModel";
import { CreateEmailPlanPreviewModal } from "./components/CreateEmailPlanPreviewModal";
import { createUsersColumns } from "./components/AdminUsersPage.columns";
import { useAdminPlansPage } from "./hooks/useAdminPlansPage";
import { useAdminUsers } from "./hooks/useAdminUsers";

export default function AdminUsersPage() {
  const { users, loading, search, setSearch, handleExportCSV, fetchUsers } =
    useAdminUsers();
  const { plans } = useAdminPlansPage();
  const [openCreatePreview, setOpenCreatePreview] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState<any>(null);
  const [selectedSchema, setSelectedSchema] = useState<any>(null);
  const [selectedSchemaId, setSelectedSchemaId] = useState<string | null>(null);
  const [formOptionsOpen, setFormOptionsOpen] = useState(false);
  const [formOptions, setFormOptions] = useState<SoulQuestionnaireForm[]>([]);

  const openFormSummary = useCallback((form: SoulQuestionnaireForm) => {
    const schema = form.latestForm?.json;
    const submission = form.submission?.json;
    const submissionId = form.submission?.id;

    if (!schema || !submission || !submissionId) {
      return;
    }

    setSelectedSchema(schema);
    setSelectedForm(submission);
    setSelectedSchemaId(submissionId);
    setFormOptionsOpen(false);
    setOpenModal(true);
  }, []);

  const handleViewForm = useCallback(
    (user: SoulDataModel) => {
      const questionnaireForms = user.questionnaireForms ?? [];
      const legacyForm: SoulQuestionnaireForm[] =
        user.latestForm?.json && user.submission?.json && user.submission?.id
          ? [
              {
                questionnaire: {
                  id: user.latestForm.id,
                  code: "FORMULARIO",
                  name: "Formulario",
                },
                latestForm: user.latestForm,
                submission: user.submission,
              },
            ]
          : [];
      const forms: SoulQuestionnaireForm[] =
        questionnaireForms.length > 0 ? questionnaireForms : legacyForm;
      const viewableForms = forms.filter(
        (item) =>
          item.latestForm?.json && item.submission?.json && item.submission?.id,
      );

      if (forms.length === 0 || viewableForms.length === 0) {
        return;
      }

      if (forms.length === 1 && viewableForms.length === 1) {
        openFormSummary(viewableForms[0]);
        return;
      }

      setFormOptions(forms);
      setFormOptionsOpen(true);
    },
    [openFormSummary],
  );

  const columns = useMemo(
    () =>
      createUsersColumns({
        onViewForm: handleViewForm,
      }),
    [handleViewForm],
  );

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        margin: "24px",
      }}
    >
      <CardContent>
        <Stack spacing={3}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="h4" fontWeight={800} color="text.primary">
                Usuarios
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Visualiza usuarios, asigna planes y revisa formularios desde un
                solo lugar.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="contained"
                size="large"
                onClick={() => setOpenCreatePreview(true)}
                sx={{
                  borderRadius: 3,
                  px: 2.5,
                  py: 1.1,
                  boxShadow: `0 16px 28px ${alpha("#032B66", 0.22)}`,
                }}
              >
                Asignar plan
              </Button>
              <Button
                startIcon={<FileDownload />}
                variant="outlined"
                size="large"
                onClick={handleExportCSV}
                sx={{
                  borderRadius: 3,
                  px: 2.5,
                  py: 1.1,
                }}
              >
                Exportar CSV
              </Button>
            </Stack>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 4,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              bgcolor: "rgba(255,255,255,0.82)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
              >
                <TextField
                  size="small"
                  placeholder="Buscar por nombre, correo, pasaporte o plan..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  sx={{ maxWidth: { md: 560 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={`${users.length} usuarios`}
                    variant="outlined"
                    sx={{ borderRadius: 999 }}
                  />
                  <Chip
                    label={`${plans.length} planes`}
                    variant="outlined"
                    sx={{ borderRadius: 999 }}
                  />
                </Stack>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                Filtra y navega el listado sin perder de vista el contexto de
                planes asociados.
              </Typography>
            </Stack>
          </Paper>

          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <DataTable
              keyField="userId"
              columns={columns}
              data={users}
              pagination
              paginationResetDefaultPage
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 20, 50]}
              paginationComponentOptions={{
                rowsPerPageText: "Registros por pagina",
                rangeSeparatorText: "de",
              }}
              highlightOnHover
              striped
              responsive
              persistTableHead
              pointerOnHover
              noDataComponent="No hay usuarios para mostrar"
              customStyles={{
                table: {
                  style: { width: "100%" },
                },
                headCells: {
                  style: {
                    fontWeight: 700,
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  },
                },
                cells: {
                  style: {
                    fontSize: "14px",
                  },
                },
                rows: {
                  style: {
                    minHeight: "54px",
                  },
                },
              }}
            />
          )}
        </Stack>
      </CardContent>

      {selectedForm && selectedSchema && selectedSchemaId && (
        <FormSummaryModal
          open={openModal}
          onClose={async () => {
            setOpenModal(false);
            setSelectedForm(null);
            setSelectedSchema(null);
            setSelectedSchemaId(null);
            await fetchUsers();
          }}
          schema={selectedSchema}
          submission={selectedForm}
          submissionId={selectedSchemaId}
        />
      )}

      <Dialog
        open={formOptionsOpen}
        onClose={() => setFormOptionsOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Seleccionar formulario
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List disablePadding>
            {formOptions.map((item, index) => {
              const canOpen =
                Boolean(item.latestForm?.json) &&
                Boolean(item.submission?.json) &&
                Boolean(item.submission?.id);
              const status =
                item.submission?.finishedAt ||
                item.submission?.status === "COMPLETED"
                  ? "Finalizado"
                  : item.submission?.json
                    ? "En proceso"
                    : "No iniciado";

              return (
                <Box
                  key={`${item.questionnaire.code}-${item.submission?.id ?? index}`}
                >
                  <ListItemButton
                    disabled={!canOpen}
                    onClick={() => openFormSummary(item)}
                    sx={{ py: 1.5, px: 3 }}
                  >
                    <ListItemText
                      primary={
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography fontWeight={700}>
                            {item.questionnaire.name || item.questionnaire.code}
                          </Typography>
                          <Chip
                            label={status}
                            size="small"
                            color={
                              status === "Finalizado"
                                ? "success"
                                : status === "En proceso"
                                  ? "warning"
                                  : "default"
                            }
                            sx={{ fontWeight: 700 }}
                          />
                        </Stack>
                      }
                      secondary={
                        item.questionnaire.description ||
                        item.questionnaire.code ||
                        "Formulario asignado"
                      }
                    />
                  </ListItemButton>
                  {index < formOptions.length - 1 && <Divider />}
                </Box>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>

      <CreateEmailPlanPreviewModal
        open={openCreatePreview}
        onClose={async () => {
          setOpenCreatePreview(false);
          await fetchUsers();
        }}
        users={users}
        plans={plans}
      />
    </Card>
  );
}
